import {
	infiniteQueryOptions,
	queryOptions,
	useQuery,
	useSuspenseInfiniteQuery,
	useSuspenseQuery,
} from '@tanstack/react-query'
import { getRouteApi } from '@tanstack/react-router'

import { successStaleTime } from '@/shared/api/utils/query'
import { useCurrentTenant } from '@/shared/auth/store'
import { time } from '@/shared/datetime/utils/time'
import { fetchFieldCommonValues } from '@/shared/opensearch/api'
import { dedupeHistogramBuckets } from '@/shared/opensearch/transforms/response'
import { resolveTimeRange } from '@/shared/opensearch/utils/time-preset'

import {
	getIntegrationKeys,
	getSavedSearches,
	getSingleSavedSearch,
	searchLogs,
} from './api'
import { prettyfySearchParams } from './utils'

import type { SearchIntegrations, SearchParams } from './types'
import type { HistogramBucket } from '@/shared/opensearch/types/response'

const Route = getRouteApi('/_dashboard/search/')

/**
 * Query keys for search logs
 */
export const searchLogsKeys = {
	all: ['search-logs'] as const,

	search: () => [...searchLogsKeys.all, 'search'] as const,
	searchLogs: (tenant: string, searchParams: SearchParams) =>
		[
			...searchLogsKeys.search(),
			tenant,
			prettyfySearchParams(searchParams),
		] as const,

	eventKeys: () => [...searchLogsKeys.all, 'event_keys'] as const,
	getKeys: (tenant: string, definition_id: string, event_type: string) =>
		[...searchLogsKeys.eventKeys(), tenant, definition_id, event_type] as const,

	fieldsUsedValues: () =>
		[...searchLogsKeys.all, 'fields-used-values'] as const,

	savedSearches: () => [...searchLogsKeys.all, 'saved-searches'] as const,
	savedSearchList: (tenant: string) =>
		[...searchLogsKeys.savedSearches(), tenant, 'list'] as const,
	savedSearchItem: (tenant: string, name: string) =>
		[...searchLogsKeys.savedSearches(), tenant, name] as const,
}

/**
 * Query options for search logs
 */
export const searchLogsQueries = {
	searchLogs: (tenant: string, searchParams: SearchParams) =>
		infiniteQueryOptions({
			queryKey: searchLogsKeys.searchLogs(tenant, searchParams),
			queryFn: ({ pageParam = 1 }) =>
				searchLogs(searchParams, tenant, pageParam),
			gcTime: time(1, 'm'),
			staleTime: ({ state }) => successStaleTime(state, 1, 'm'),
			initialPageParam: 1,
			getNextPageParam: (lastPage, _allPages, lastPageParam) => {
				const pageSize = 100

				if (lastPage.hits.hits.length < pageSize) {
					return undefined
				}

				return lastPageParam + 1
			},
			select: (data) => {
				const allHits = data.pages.flatMap((page) =>
					page.hits.hits.map((hit) => ({
						...(hit._source.event ?? {}),
						'_id': hit._id,
						'@timestamp': hit._source['@timestamp'],
					})),
				)

				const rawAggregationBuckets = data.pages.flatMap<HistogramBucket>(
					(page) => page.aggregations?.events_over_time?.buckets ?? [],
				)

				return {
					hits: allHits,
					// Dedupe + sort merged pages (shared transform — single source).
					aggregations: dedupeHistogramBuckets(rawAggregationBuckets),
				}
			},
		}),

	/**
	 * Integration event-schema fetch. Returns `{source_schema, normalized_schema?}`
	 * directly — consumers (the sidebar's `useFieldSections`) work with the
	 * raw schema and build their own FieldNode trees.
	 */
	getKeys: (tenant: string, definition_id: string, event_type: string) =>
		queryOptions({
			queryKey: searchLogsKeys.getKeys(tenant, definition_id, event_type),
			queryFn: ({ signal }) =>
				getIntegrationKeys(tenant, definition_id, event_type, signal),
			gcTime: time(1, 'h'),
			staleTime: ({ state }) => successStaleTime(state, 5, 'h'),
		}),

	/**
	 * Common-values popover query — POSTs `/logs/search` with an aggs-only
	 * body via `composeCommonValuesBody`, then `select`-transforms the raw
	 * buckets into `{value, count, percent}` rows via `bucketsToCommonValues`.
	 */
	commonValues: (
		tenant: string,
		field: string,
		integrations: SearchIntegrations,
		size: number = 10,
		enabled: boolean = false,
		context?: {
			tr?: SearchParams['tr']
			ft?: SearchParams['ft']
			q?: SearchParams['q']
		},
	) =>
		queryOptions({
			queryKey: [
				...searchLogsKeys.fieldsUsedValues(),
				'common-values',
				tenant,
				field,
				prettyfySearchParams({ int: integrations }),
				size,
				context,
			] as const,
			queryFn: () =>
				fetchFieldCommonValues(tenant, {
					field,
					sources: integrations.map((i): [string, string] => [i.id, i.ev]),
					size,
					tr: resolveTimeRange(context?.tr),
					ft: context?.ft,
					q: context?.q,
				}),
			select: (entries) => ({ entries }),
			gcTime: time(5, 'm'),
			staleTime: ({ state }) => successStaleTime(state, 1, 'm'),
			throwOnError: false,
			enabled: !!field && enabled && integrations.length > 0,
		}),

	savedSearchList: (tenant: string) =>
		queryOptions({
			queryKey: searchLogsKeys.savedSearchList(tenant),
			queryFn: ({ signal }) => getSavedSearches(tenant, signal),
			gcTime: time(1, 'h'),
			staleTime: ({ state }) => successStaleTime(state, 1, 'h'),
			throwOnError: false,
		}),

	savedSearchItem: (tenant: string, name: string) =>
		queryOptions({
			queryKey: searchLogsKeys.savedSearchItem(tenant, name),
			queryFn: ({ signal }) => getSingleSavedSearch(tenant, name, signal),
			gcTime: time(1, 'h'),
			staleTime: ({ state }) => successStaleTime(state, 1, 'h'),
			throwOnError: false,
		}),
}

export const useSearchLogs = () => {
	const tenant = useCurrentTenant()
	const search = Route.useSearch()

	const query = useSuspenseInfiniteQuery(
		searchLogsQueries.searchLogs(tenant, search),
	)

	return {
		data: query.data,
		hits: query.data.hits,
		aggregations: query.data.aggregations,
		fetchNextPage: query.fetchNextPage,
		hasNextPage: query.hasNextPage,
		isFetchingNextPage: query.isFetchingNextPage,
		isFetching: query.isFetching,
	}
}

/**
 * Common-values popover hook. Sends full int + tr + ft + q context so the
 * counts reflect what the user is actually looking at (fixes v1 defect).
 */
export const useCommonValues = (
	field: string,
	enabled: boolean = false,
	size: number = 10,
) => {
	const tenant = useCurrentTenant()
	const search = Route.useSearch()

	return useQuery(
		searchLogsQueries.commonValues(
			tenant,
			field,
			search.int ?? [],
			size,
			enabled,
			{ tr: search.tr, ft: search.ft, q: search.q },
		),
	)
}

/**
 * Use the saved search list query
 */
export const useSavedSearchList = () => {
	const tenant = useCurrentTenant()

	return useSuspenseQuery(searchLogsQueries.savedSearchList(tenant))
}

/**
 * Use the saved search list query
 */
export const useSavedSearchItem = (name: string) => {
	const tenant = useCurrentTenant()

	return useSuspenseQuery(searchLogsQueries.savedSearchItem(tenant, name))
}
